import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Save, ShieldCheck, Info } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useCreateEmailCategoryMutation,
  useLazyGetEmailCategoryByIdQuery,
  useUpdateEmailCategoryMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { ICreateEmailCategoryRequest } from "../../../types/Request/AdminRequest";

const NAVIGATE_TARGET = "/admin/adminemailtemplates/categories";

const EmailCategoryDetailsScreen: React.FC = () => {
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const isEdit = Boolean(id);

  const [getCategoryById, { data: category, isFetching, isError }] =
    useLazyGetEmailCategoryByIdQuery();

  const [createCategory, { isLoading: isCreating }] =
    useCreateEmailCategoryMutation();

  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateEmailCategoryMutation();

  const [form, setForm] = useState<ICreateEmailCategoryRequest>({
    name: "",
    displayName: "",
    description: "",
    sortOrder: 1,
    isActive: true,
  });

  const [errorMessage, setErrorMessage] = useState("");

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (isEdit && id) {
      getCategoryById(Number(id));
    }
  }, [id, isEdit, getCategoryById]);

  useEffect(() => {
    if (!category) {
      return;
    }

    setForm({
      name: category.name,
      displayName: category.displayName,
      description: category.description ?? "",
      sortOrder: category.sortOrder,
      isActive: category.isActive,
    });
  }, [category]);

  const updateField = <K extends keyof ICreateEmailCategoryRequest>(
    field: K,
    value: ICreateEmailCategoryRequest[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBack = () => {
    navigate(NAVIGATE_TARGET);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrorMessage("");

    if (!form.name.trim()) {
      setErrorMessage("Category name is required.");
      return;
    }

    if (!form.displayName.trim()) {
      setErrorMessage("Display name is required.");
      return;
    }

    try {
      if (isEdit && category) {
        await updateCategory({
          id: category.id,
          data: {
            displayName: form.displayName.trim(),

            description: form.description.trim(),

            sortOrder: Number(form.sortOrder),

            isActive: form.isActive,
          },
        }).unwrap();
      } else {
        await createCategory({
          name: form.name.trim(),

          displayName: form.displayName.trim(),

          description: form.description.trim(),

          sortOrder: Number(form.sortOrder),

          isActive: form.isActive,
        }).unwrap();
      }

      navigate(NAVIGATE_TARGET);
    } catch (error: any) {
      console.error("Email category save failed:", error);

      setErrorMessage(
        error?.data?.message ||
          error?.data?.Message ||
          "Unable to save email category.",
      );
    }
  };

  // --------------------------------------------------
  // Loading
  // --------------------------------------------------

  if (isEdit && isFetching && !category) {
    return (
      <div className="flex min-h-[500px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-primary" />

          <p className="text-sm text-slate-500">Loading email category...</p>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // Not Found
  // --------------------------------------------------

  if (isEdit && (isError || (!isFetching && !category))) {
    return (
      <div className="flex min-h-[500px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-red-50 text-red-500">
          <Info size={26} />
        </div>

        <h2 className="mt-4 text-lg font-semibold text-slate-900">
          Category Not Found
        </h2>

        <p className="mt-1 max-w-md text-sm text-slate-500">
          The email category you are trying to edit does not exist or may have
          been removed.
        </p>

        <button
          type="button"
          onClick={handleBack}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          <ArrowLeft size={16} />
          Back to Categories
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50/40">
      {/* ========================================================= */}
      {/* HEADER */}
      {/* ========================================================= */}

      <div className="border-b border-slate-200 bg-white">
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
              title="Back to Categories"
            >
              <ArrowLeft
                size={18}
                className="transition-transform group-hover:-translate-x-0.5"
              />
            </button>

            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {isEdit ? "Edit Email Category" : "Create Email Category"}
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                {isEdit
                  ? "Update the display information and settings for this email category."
                  : "Create a category to organize your email templates."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* FORM */}
      {/* ========================================================= */}

      <div className="p-6">
        <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Accent */}
          <div className="h-1.5 w-full bg-gradient-to-r from-primary via-indigo-500 to-sky-500" />

          {/* Saving overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
              <div className="flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-lg">
                <Loader2 size={18} className="animate-spin text-primary-400" />

                <span className="text-sm font-medium">
                  {isEdit ? "Saving changes..." : "Creating category..."}
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            {/* ================================================= */}
            {/* BASIC INFORMATION */}
            {/* ================================================= */}

            <div>
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <ShieldCheck size={19} />
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Category Information
                  </h2>

                  <p className="text-xs text-slate-500">
                    Configure how this category appears in email management.
                  </p>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                {/* Name */}
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    Category Name
                    {!isEdit && <span className="ml-1 text-red-500">*</span>}
                  </label>

                  <input
                    type="text"
                    value={form.name}
                    disabled={isEdit}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="e.g. Notifications"
                    className={`h-11 w-full rounded-xl border px-3 text-sm outline-none transition ${
                      isEdit
                        ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500"
                        : "border-slate-200 bg-white text-slate-700 focus:border-primary focus:ring-2 focus:ring-primary/10"
                    }`}
                  />

                  <p className="mt-1.5 text-[11px] text-slate-400">
                    {isEdit
                      ? "Category name cannot be changed after creation."
                      : "Internal identifier used by the system."}
                  </p>
                </div>

                {/* Display Name */}
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    Display Name
                    <span className="ml-1 text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    value={form.displayName}
                    onChange={(e) => updateField("displayName", e.target.value)}
                    placeholder="e.g. Notifications"
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
                  />

                  <p className="mt-1.5 text-[11px] text-slate-400">
                    Name displayed to administrators.
                  </p>
                </div>
              </div>
            </div>

            {/* ================================================= */}
            {/* DESCRIPTION + SORT */}
            {/* ================================================= */}

            <div className="mt-8 border-t border-slate-100 pt-8">
              <div className="grid gap-5 sm:grid-cols-3">
                {/* Description */}
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    Description
                  </label>

                  <textarea
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    rows={4}
                    placeholder="Describe what this category is used for..."
                    className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-700">
                    Sort Order
                  </label>

                  <input
                    type="number"
                    min={0}
                    value={form.sortOrder}
                    onChange={(e) =>
                      updateField("sortOrder", Number(e.target.value))
                    }
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
                  />

                  <p className="mt-1.5 text-[11px] text-slate-400">
                    Lower values appear first.
                  </p>
                </div>
              </div>
            </div>

            {/* ================================================= */}
            {/* STATUS */}
            {/* ================================================= */}

            <div className="mt-8 border-t border-slate-100 pt-8">
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Active Category
                  </p>

                  <p className="mt-0.5 text-xs text-slate-500">
                    Inactive categories can remain configured without being
                    actively used.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => updateField("isActive", !form.isActive)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    form.isActive ? "bg-[#2E7D32]" : "bg-slate-300"
                  }`}
                  aria-label="Toggle category status"
                >
                  <span
                    className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${
                      form.isActive ? "left-6" : "left-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* ================================================= */}
            {/* SYSTEM CATEGORY INFO */}
            {/* ================================================= */}

            {isEdit && category?.isSystem && (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <ShieldCheck
                  size={18}
                  className="mt-0.5 shrink-0 text-blue-600"
                />

                <div>
                  <p className="text-xs font-semibold text-blue-800">
                    System Category
                  </p>

                  <p className="mt-1 text-xs leading-5 text-blue-700">
                    This is a system-managed category. Its internal name cannot
                    be changed and the category cannot be deleted.
                  </p>
                </div>
              </div>
            )}

            {/* ================================================= */}
            {/* ERROR */}
            {/* ================================================= */}

            {errorMessage && (
              <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {errorMessage}
              </div>
            )}

            {/* ================================================= */}
            {/* ACTIONS */}
            {/* ================================================= */}

            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowLeft size={16} />
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#2E7D32] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#256B2A] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <Loader2 size={17} className="animate-spin" />
                ) : (
                  <Save size={17} />
                )}

                {isEdit ? "Save Changes" : "Create Category"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailCategoryDetailsScreen;
